import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cur } from '@/lib/currency';
import api from '@/lib/api';

const n2 = (v) => Number(v || 0).toFixed(2);

function sums(list) {
  const t = list.filter((l) => (l.direction || 'taken') === 'taken');
  const g = list.filter((l) => l.direction === 'given');
  const s = (a, k) => a.reduce((x, l) => x + Number(l[k] || 0), 0);
  return { taken: s(t, 'total_amount'), given: s(g, 'total_amount'), repaid: s(t, 'paid_amount'), recovered: s(g, 'paid_amount'),
    payable: s(t, 'remaining'), receivable: s(g, 'remaining') };
}

function Cells({ s }) {
  return ['taken', 'given', 'repaid', 'recovered', 'payable', 'receivable'].map((k) => (
    <td key={k} className={`p-2 text-right tabular-nums ${k === 'payable' ? 'text-error' : k === 'receivable' ? 'text-success' : ''}`}>{cur()} {n2(s[k])}</td>
  ));
}

function LoanLine({ loan }) {
  const [pays, setPays] = useState(null);
  const load = async () => { if (pays) { setPays(null); return; } const r = await api.get(`/company-loan-payments?loan_id=${loan.id}`); setPays(r.data || []); };
  return (
    <>
      <tr className="border-b cursor-pointer hover:bg-muted/30 text-xs" onClick={load} data-testid={`loan-line-${loan.id}`}>
        <td className="p-2 pl-10">{(loan.direction || 'taken') === 'taken' ? 'From' : 'To'} {loan.lender} <span className="text-muted-foreground">({loan.loan_type}{loan.classification ? `, ${loan.classification.term}-term${loan.classification.verified ? '' : ' — term unverified'}` : ''})</span></td>
        <Cells s={sums([loan])} />
      </tr>
      {pays && (
        <tr className="border-b"><td colSpan={7} className="p-2 pl-14 text-[11px] text-muted-foreground" data-testid={`loan-payments-${loan.id}`}>
          {pays.length ? pays.map((p) => `${String(p.date || p.created_at).slice(0, 10)} ${cur()} ${n2(p.amount)} (${p.payment_mode || ''})`).join(' · ') : 'No payments yet'}
        </td></tr>
      )}
    </>
  );
}

// Optional grouped view: per branch, taken and given kept in separate columns; each loan counted once.
export function LoansBranchSummary({ loans, branches }) {
  const [open, setOpen] = useState({});
  const name = Object.fromEntries((branches || []).map((b) => [b.id, b.name]));
  const by = {};
  loans.forEach((l) => { (by[l.branch_id || 'none'] ||= []).push(l); });
  const keys = Object.keys(by).sort((a, b) => (name[a] || 'zz').localeCompare(name[b] || 'zz'));
  return (
    <Card className="border-border" data-testid="loans-branch-summary"><CardContent className="pt-4 overflow-x-auto">
      <table className="w-full text-sm"><thead><tr className="border-b text-xs text-muted-foreground">
        <th className="p-2 text-left">Branch / lender or borrower</th><th className="p-2 text-right">Loans taken</th><th className="p-2 text-right">Loans given</th>
        <th className="p-2 text-right">Repayments</th><th className="p-2 text-right">Recoveries</th><th className="p-2 text-right">Payable</th><th className="p-2 text-right">Receivable</th>
      </tr></thead><tbody>
        {keys.map((b) => (
          <Fragment key={b}>
            <tr className="border-b bg-muted/40 cursor-pointer font-medium" onClick={() => setOpen((p) => ({ ...p, [b]: !p[b] }))} data-testid={`loans-branch-${b}`}>
              <td className="p-2">{open[b] ? <ChevronDown size={14} className="inline mr-1" /> : <ChevronRight size={14} className="inline mr-1" />}{name[b] || 'Company level (no branch)'}</td>
              <Cells s={sums(by[b])} />
            </tr>
            {open[b] && by[b].map((l) => <LoanLine key={l.id} loan={l} />)}
          </Fragment>
        ))}
        <tr className="border-t-2 font-semibold" data-testid="loans-branch-total"><td className="p-2">All branches</td><Cells s={sums(loans)} /></tr>
      </tbody></table>
    </CardContent></Card>
  );
}
